import { VFC, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { MasterDataStore, useMasterData } from 'src/api/masterData'
import { updateResponsibilities } from 'src/api/user/mutations'
import { asModal } from 'src/components/hoc/asModal'
import { CATEGORY_DICT } from 'src/data/masterData'
import { MasterData, MasterDataWithCategory } from 'src/models/MasterData'
import { FormItemProps } from './common'
import { FeaturesSelection } from './FeaturesSelection'
import { FormSelectedItems } from './FormSelectedItems'
import { Selection } from './Selection'

type FormMultiSelectProps = FormItemProps & {
  description: string
  onlineMtgAvailable: boolean | null
  message: string | null
}

type Item = MasterData & {
  category?: string
  subCategory?: string
}

export const convertItems = (items: Item[]): MasterDataWithCategory[] =>
  items
    .filter((item) => item.category !== 'connection') // 「つながり」は除外する
    .map((item) => {
      return {
        value: item.value,
        label: item.label,
        category: item.category ? CATEGORY_DICT[item.category] : '',
        subCategory: item.subCategory,
      }
    })

export const MasterDataMultiSelectWithModal: VFC<
  FormMultiSelectProps & { type: keyof MasterDataStore }
> = ({ type, description, ...props }) => {
  const { id, label, onlineMtgAvailable, message } = props
  const [openModal, setOpenModal] = useState(false)
  const { data } = useMasterData()
  const options = data ? data[type] : []
  const { getValues } = useFormContext()
  return (
    <>
      <FormSelectedItems
        id={id}
        label={label}
        items={getValues(id)
          .filter(
            (item: MasterDataWithCategory) => item.category !== 'connection',
          )
          .map((item: MasterData) => item.label)}
        setIsOpen={() => setOpenModal(true)}
      />
      {openModal && (
        <MultiSelectModal
          id={id}
          label={label}
          description={description}
          close={() => setOpenModal(false)}
          items={convertItems(options)}
          onlineMtgAvailable={onlineMtgAvailable}
          message={message}
        />
      )}
    </>
  )
}

export const MultiSelectModal = asModal<{
  id: string
  label: string
  description: string
  onlineMtgAvailable: boolean | null
  message: string | null
  items: MasterDataWithCategory[]
  close: () => void
}>(
  ({ id, label, description, message, onlineMtgAvailable, items, close }) => {
    const { setValue, getValues } = useFormContext()
    const categories = Array.from(new Set(items.map((s) => s.category))).filter(
      (v) => v,
    )
    const [selectedCategory, setSelectedCategory] = useState(categories[0])
    const [selectedItems, setSelectedItems] = useState(getValues(id))
    const [errorMessages, setErrorMessages] = useState('')

    const handleOnSubmit = async () => {
      // NOTE: formのState更新
      setValue(id, selectedItems)

      // NOTE: getValues()を引数に入れると型エラーになるので、展開して連想配列を引数に入れている。
      // domainsとfeaturesのみ更新する。message, onlineMtgAvailableは更新しないため、stateではなくuserの値をそのまま渡す
      const res = await updateResponsibilities({
        domains: getValues('domains'),
        features: getValues('features'),
        message: message,
        onlineMtgAvailable: onlineMtgAvailable || true,
      })

      if (res.data) close()
      if (res.error?.errorMessages)
        setErrorMessages(res.error?.errorMessages[0])
    }

    // NOTE: featuresの場合はsubCategoryでグループ化する専用コンポーネントを使用
    const hasSubCategories = items.some((item) => item.subCategory)

    if (hasSubCategories)
      return (
        <FeaturesSelection
          title={label}
          description={description}
          items={items}
          selectedItems={selectedItems}
          setSelectedItems={setSelectedItems}
          handleOnSubmit={handleOnSubmit}
          errorMessages={errorMessages}
        />
      )

    return (
      <Selection
        title={label}
        description={description}
        categories={categories}
        selectedCategory={selectedCategory}
        setSelectedCategory={setSelectedCategory}
        items={items}
        selectedItems={selectedItems}
        setSelectedItems={setSelectedItems}
        handleOnSubmit={handleOnSubmit}
        errorMessages={errorMessages}
      />
    )
  },
  { scrollable: true },
)
